import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ImageNotSupported as NoImageIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable } from '../../components/common';
import { adminApi } from '../../utils/api';
import { format } from 'date-fns';

const Recipes = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchRecipes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.getRecipes({
        search,
        page: page + 1,
        limit: pageSize,
        owner: ownerFilter !== 'all' ? ownerFilter : undefined,
      });
      setRecipes(data.recipes || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to fetch recipes:', err);
      // Show empty state on error - no mock data
      setRecipes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize, ownerFilter]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const handleBulkDelete = async () => {
    try {
      await adminApi.bulkDeleteRecipes(selected);
      setSelected([]);
      fetchRecipes();
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Failed to delete recipes:', err);
    }
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Recipe',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              backgroundColor: theme.palette.background.elevated,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {params.row.image_url ? (
              <Box
                component="img"
                src={params.row.image_url}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <NoImageIcon
              sx={{
                color: theme.palette.text.disabled,
                display: params.row.image_url ? 'none' : 'block',
                fontSize: 20,
              }}
            />
          </Box>
          <Box sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
            {params.row.title}
          </Box>
        </Box>
      ),
    },
    {
      field: 'user_email',
      headerName: 'Owner',
      width: 180,
      renderCell: (params) =>
        params.row.user_email ? (
          <Chip
            label={params.row.user_email.split('@')[0]}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        ) : (
          <Chip
            label="Public"
            size="small"
            sx={{
              backgroundColor: `${theme.palette.success.main}20`,
              color: theme.palette.success.main,
              fontSize: '0.7rem',
            }}
          />
        ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(params.row.tags || []).slice(0, 2).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                backgroundColor: `${theme.palette.primary.main}15`,
              }}
            />
          ))}
          {(params.row.tags?.length || 0) > 2 && (
            <Chip
              label={`+${params.row.tags.length - 2}`}
              size="small"
              sx={{ height: 22, fontSize: '0.68rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'image_url',
      headerName: 'Image',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.image_url ? (
          <ImageIcon sx={{ color: theme.palette.success.main }} />
        ) : (
          <NoImageIcon sx={{ color: theme.palette.text.disabled }} />
        ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      renderCell: (params) =>
        params.row.created_at
          ? format(new Date(params.row.created_at), 'MMM d, yyyy')
          : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Delete recipe">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelected([params.row.id]);
              setDeleteDialogOpen(true);
            }}
            sx={{ color: theme.palette.text.secondary }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="recipes"
        subtitle={`${totalCount} total recipes`}
        breadcrumbs={[{ label: 'recipes' }]}
        action={
          selected.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete ({selected.length})
            </Button>
          )
        }
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search recipes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Filter by owner">
          <IconButton onClick={(e) => setFilterAnchor(e.currentTarget)}>
            <FilterIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
        >
          <MenuItem
            selected={ownerFilter === 'all'}
            onClick={() => {
              setOwnerFilter('all');
              setFilterAnchor(null);
            }}
          >
            All Recipes
          </MenuItem>
          <MenuItem
            selected={ownerFilter === 'public'}
            onClick={() => {
              setOwnerFilter('public');
              setFilterAnchor(null);
            }}
          >
            Public Only
          </MenuItem>
          <MenuItem
            selected={ownerFilter === 'user'}
            onClick={() => {
              setOwnerFilter('user');
              setFilterAnchor(null);
            }}
          >
            User Owned
          </MenuItem>
        </Menu>
      </Box>

      {/* Data Table */}
      <DataTable
        rows={recipes}
        columns={columns}
        loading={loading}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => navigate(`/recipes/${row.id}`)}
        checkboxSelection
        onSelectionChange={setSelected}
        getRowId={(row) => row.id}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          Are you sure you want to delete {selected.length} recipe(s)?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Recipes;
